import { googleOAuthReady } from "@/lib/google-oauth.mjs";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { ExternalLink, CircleCheck, CircleDashed, Loader2 } from "lucide-react";
import { getGoogleConfig, startGoogleAuth, getToken } from "@/lib/api";

const STEPS = [
  { title: "Use the Google account that manages your site", body: "Choose an account with access to your website in Google Search Console and Google Analytics 4." },
  { title: "Allow read-only access", body: "Google will ask you to let SEO Signals read your Search Console and Analytics data. SEO Signals cannot change your site or your Google settings." },
  { title: "Choose your site", body: "Your verified sites will appear in the dashboard. If Analytics is not matched automatically, use the GA4 property picker to choose the right property." },
];

export default function SetupGuide({ trigger, open: openProp, onOpenChange }) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = openProp ?? uncontrolledOpen;
  const setOpen = openProp !== undefined ? (onOpenChange ?? (() => {})) : setUncontrolledOpen;
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    getGoogleConfig().then(c => { if (!cancelled) setConfig(c); }).catch(() => { if (!cancelled) setConfig({configured:false}); });
    return () => { cancelled = true; };
  }, [open]);
  const intent = getToken() && localStorage.getItem("sp_demo") !== "1" ? "connect" : "login";
  const ready = googleOAuthReady(config, intent);
  const connect = async () => {
    setBusy(true);
    try { await startGoogleAuth(config, intent); }
    catch { toast.error("Google sign-in couldn't start. Please try again."); setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white p-0 gap-0">
        <DialogHeader className="border-b border-slate-200 bg-white px-6 py-4">
          <DialogTitle className="font-heading font-bold text-xl text-navy tracking-tight">Connect your Google data</DialogTitle>
          <DialogDescription className="text-sm text-navy/60 mt-1">Give SEO Signals read-only access to Search Console and Google Analytics. We list the properties your account can access so you can choose reports and sites to enrol for history. SEO Signals cannot edit or publish anything in your Google account. Connecting Google does not enable sharing with an AI provider. You can disconnect Google in Settings.</DialogDescription>
          {config && <div data-testid="setup-status" className="mt-3 inline-flex items-center gap-2 text-sm text-navy/70">
            {ready ? <CircleCheck size={14} /> : <CircleDashed size={14} />}
            {ready ? (intent === "connect" ? "Google connection is available" : "Google sign-in is available") : "Google connection setup is in progress. You can still explore the demo."}
          </div>}
        </DialogHeader>
        <p className="px-6 pt-4 text-sm"><a href="/privacy">Privacy policy</a></p>
        <ol className="px-6 py-5 space-y-5">
          {STEPS.map((step, i) => <li key={step.title} className="flex gap-4" data-testid={`setup-step-${i}`}>
            <div className="grid place-items-center h-7 w-7 rounded-full bg-navy text-white font-heading font-bold text-sm shrink-0">{i + 1}</div>
            <div><h3 className="font-heading font-semibold text-navy">{step.title}</h3><p className="mt-1 text-sm text-navy/65 leading-relaxed">{step.body}</p></div>
          </li>)}
        </ol>
        <div className="px-6 pb-6 space-y-4">
          <button type="button" onClick={connect} disabled={!ready || busy} className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-emerald px-5 py-3 font-semibold text-white hover:bg-emerald-dark disabled:opacity-50">
            {busy && <Loader2 size={16} className="animate-spin" aria-hidden="true" />} {intent === "connect" ? "Connect Search Console and Analytics" : "Sign in first"}
          </button>
          <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-dark hover:underline">Open Google Search Console <ExternalLink size={13} aria-hidden="true" /></a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
